// Convert numbers to Spanish words (Argentina convention)
// Used in contracts to express amounts in letters (e.g., "quince mil pesos")

const UNIDADES = [
  '',
  'uno',
  'dos',
  'tres',
  'cuatro',
  'cinco',
  'seis',
  'siete',
  'ocho',
  'nueve',
  'diez',
  'once',
  'doce',
  'trece',
  'catorce',
  'quince',
  'dieciséis',
  'diecisiete',
  'dieciocho',
  'diecinueve',
  'veinte',
]

const DECENAS = [
  '',
  '',
  'veinti',
  'treinta',
  'cuarenta',
  'cincuenta',
  'sesenta',
  'setenta',
  'ochenta',
  'noventa',
]

const CENTENAS = [
  '',
  'ciento',
  'doscientos',
  'trescientos',
  'cuatrocientos',
  'quinientos',
  'seiscientos',
  'setecientos',
  'ochocientos',
  'novecientos',
]

const convertirGrupo = (n: number): string => {
  if (n === 0) return ''
  if (n === 100) return 'cien'

  let texto = ''
  const centenas = Math.floor(n / 100)
  const resto = n % 100

  if (centenas > 0) {
    texto += CENTENAS[centenas]
  }

  if (resto > 0) {
    if (centenas > 0) texto += ' '

    if (resto <= 20) {
      texto += UNIDADES[resto]
    } else {
      const decenas = Math.floor(resto / 10)
      const unidades = resto % 10

      if (decenas === 2) {
        // 21-29: veintiuno, veintidós, etc.
        texto += DECENAS[decenas] + (unidades > 0 ? UNIDADES[unidades] : '')
      } else {
        texto += DECENAS[decenas]
        if (unidades > 0) {
          texto += ' y ' + UNIDADES[unidades]
        }
      }
    }
  }

  return texto
}

/**
 * Convert a number to its Spanish text representation.
 * @param num - Integer number (decimals are rounded)
 * @returns Spanish text (e.g., 15000 → "quince mil")
 */
export const numeroALetras = (num: number): string => {
  if (num === 0) return 'cero'
  if (num < 0) return 'menos ' + numeroALetras(Math.abs(num))

  const entero = Math.round(num)

  if (entero < 1000) {
    return convertirGrupo(entero)
  }

  if (entero < 1_000_000) {
    const miles = Math.floor(entero / 1000)
    const resto = entero % 1000
    let texto = ''

    if (miles === 1) {
      texto = 'mil'
    } else {
      texto = convertirGrupo(miles) + ' mil'
    }

    if (resto > 0) {
      texto += ' ' + convertirGrupo(resto)
    }

    return texto
  }

  if (entero < 1_000_000_000) {
    const millones = Math.floor(entero / 1_000_000)
    const resto = entero % 1_000_000
    let texto = ''

    if (millones === 1) {
      texto = 'un millón'
    } else {
      texto = convertirGrupo(millones) + ' millones'
    }

    if (resto > 0) {
      if (resto < 1000) {
        texto += ' ' + convertirGrupo(resto)
      } else {
        texto += ' ' + numeroALetras(resto)
      }
    }

    return texto
  }

  // Fallback for very large numbers
  return entero.toLocaleString('es-AR')
}

/**
 * Format a price as "pesos {letras} (${numero})" for contracts.
 * @param amount - Amount in pesos
 * @returns e.g., "pesos quince mil ($15.000)"
 */
export const formatPrecioContrato = (amount: number): string => {
  const letras = numeroALetras(amount)
  const numero = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
  }).format(amount)
  return `pesos ${letras} (${numero})`
}
